import { z } from 'zod';
import { uploadedImageSchema, statusTagEnum } from './product-submission';

// Schema for PreviewImageDto (from API response format)
// imageUrl/thumbnailUrl are display-only (only imageKey is submitted) and the
// backend GET may return relative/CDN paths, so they must not require an
// absolute URL — otherwise editing such a product fails validation silently.
export const previewImageDtoSchema = z.object({
  imageKey: z.string(),
  imageUrl: z.string(),
  thumbnailUrl: z.string(),
});

// ============================================================================
// BLOCK CONTENT SCHEMAS
// ============================================================================

export const textBlockSchema = z.object({
  id: z.string(),
  type: z.literal('text'),
  createdAt: z.number(),
  updatedAt: z.number(),
  content: z.object({
    html: z.string(),
    plainText: z.string(),
  }),
  style: z.object({
    variant: z.enum(['paragraph', 'heading1', 'heading2', 'heading3', 'quote']),
  }),
});

export const imageBlockSchema = z.object({
  id: z.string(),
  type: z.literal('image'),
  createdAt: z.number(),
  updatedAt: z.number(),
  content: z.object({
    image: uploadedImageSchema.nullable(),
    caption: z.string().optional(),
    alt: z.string().optional(),
  }),
  style: z.object({
    size: z.enum(['small', 'medium', 'large', 'full']),
    alignment: z.enum(['left', 'center', 'right']),
  }),
});

export const videoBlockSchema = z.object({
  id: z.string(),
  type: z.literal('video'),
  createdAt: z.number(),
  updatedAt: z.number(),
  content: z.object({
    url: z.string(),
    embedId: z.string(),
    provider: z.enum(['youtube', 'vimeo']),
    caption: z.string().optional(),
    embedSrc: z.string().url().optional(), // Custom embed player src URL
    inputMode: z.enum(['url', 'iframe']).optional(), // Track input method
    aspectRatio: z.number().positive().optional(), // Intrinsic ratio parsed from embed
    scalePercent: z.number().min(100).max(200).optional(), // Zoom inside the frame
    offsetX: z.number().min(-50).max(50).optional(), // Pan, % of frame
    offsetY: z.number().min(-50).max(50).optional(),
  }),
});

export const galleryBlockSchema = z.object({
  id: z.string(),
  type: z.literal('gallery'),
  createdAt: z.number(),
  updatedAt: z.number(),
  content: z.object({
    images: z.array(uploadedImageSchema),
    captions: z.array(z.string()).optional(),
  }),
  style: z.object({
    columns: z.union([z.literal(2), z.literal(3), z.literal(4)]),
  }),
});

export const contentBlockSchema = z.discriminatedUnion('type', [
  textBlockSchema,
  imageBlockSchema,
  videoBlockSchema,
  galleryBlockSchema,
]);

// ============================================================================
// HEADER SCHEMA
// ============================================================================

export const pageBuilderHeaderSchema = z.object({
  slug: z
    .string()
    .min(1, 'Slug обязателен')
    .max(60, 'Slug не должен превышать 60 символов')
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Только строчные латинские буквы, цифры и дефис')
    .trim(),
  name: z
    .string()
    .min(1, 'Название обязательно')
    .max(50, 'Название не должно превышать 50 символов')
    .trim(),
  tagline: z
    .string()
    .min(6, 'Слоган должен содержать минимум 6 символов')
    .max(100, 'Слоган не должен превышать 100 символов')
    .trim(),
  description: z
    .string()
    .min(20, 'Описание должно содержать минимум 20 символов')
    .max(500, 'Описание не должно превышать 500 символов')
    .trim(),
  logo: uploadedImageSchema.nullable().refine(
    (val) => val !== null,
    { message: 'Загрузите логотип' }
  ),
  websiteUrl: z
    .string()
    .url('Введите корректный URL')
    .trim()
    .optional()
    .or(z.literal('')),
});

// ============================================================================
// METADATA SCHEMA
// ============================================================================

export const pageBuilderMetadataSchema = z.object({
  categoryIds: z
    .array(z.number())
    .min(1, 'Выберите минимум 1 категорию')
    .max(3, 'Максимум 3 категории'),
  statusTags: z
    .array(statusTagEnum)
    .min(1, 'Выберите минимум 1 вариант'),
  previewImages: z
    .array(previewImageDtoSchema)
    .max(5, 'Максимум 5 изображений')
    .optional(),
});

// ============================================================================
// FULL FORM SCHEMA
// ============================================================================

export const pageBuilderFormSchema = z.object({
  header: pageBuilderHeaderSchema,
  blocks: z
    .array(contentBlockSchema)
    .min(1, 'Добавьте хотя бы один блок контента'),
  metadata: pageBuilderMetadataSchema,
});

// ============================================================================
// BLOCK-SPECIFIC VALIDATION
// ============================================================================

/**
 * Validates individual blocks for completeness
 * Returns array of error messages for incomplete blocks
 */
export function validateBlock(block: z.infer<typeof contentBlockSchema>): string[] {
  const errors: string[] = [];

  switch (block.type) {
    case 'text':
      if (!block.content.plainText.trim()) {
        errors.push('Текстовый блок не может быть пустым');
      }
      break;

    case 'image':
      if (!block.content.image) {
        errors.push('Загрузите изображение');
      }
      break;

    case 'video':
      // Video block is valid if it has either embedId (URL mode) or embedSrc (iframe mode)
      if (!block.content.embedId && !block.content.embedSrc) {
        errors.push('Укажите ссылку на видео или вставьте код iframe');
      }
      break;

    case 'gallery':
      if (block.content.images.length < 2) {
        errors.push('Добавьте минимум 2 изображения в галерею');
      }
      break;
  }

  return errors;
}

/**
 * Validates all blocks and returns a map of block ID to errors
 */
export function validateAllBlocks(
  blocks: z.infer<typeof contentBlockSchema>[]
): Map<string, string[]> {
  const errorsMap = new Map<string, string[]>();

  blocks.forEach((block) => {
    const errors = validateBlock(block);
    if (errors.length > 0) {
      errorsMap.set(block.id, errors);
    }
  });

  return errorsMap;
}

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type PageBuilderFormDataValidated = z.infer<typeof pageBuilderFormSchema>;
export type ContentBlockValidated = z.infer<typeof contentBlockSchema>;
export type TextBlockValidated = z.infer<typeof textBlockSchema>;
export type ImageBlockValidated = z.infer<typeof imageBlockSchema>;
export type VideoBlockValidated = z.infer<typeof videoBlockSchema>;
export type GalleryBlockValidated = z.infer<typeof galleryBlockSchema>;
